import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Edit3, X, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

interface FeatureComparisonCardProps {
  onDismiss?: () => void;
}

export function FeatureComparisonCard({ onDismiss }: FeatureComparisonCardProps) {
  const [isVisible, setIsVisible] = useState(true);
  const { t } = useLanguage();

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  return (
    <Card className={cn(
      "border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent",
      "animate-in fade-in-0 slide-in-from-top-2 duration-300"
    )}>
      <CardContent className="p-4 relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="absolute top-2 right-2 h-6 w-6 p-0"
        >
          <X className="h-4 w-4" />
        </Button>

        <h3 className="font-semibold text-base mb-3 pr-8">
          {t("onboarding.featureComparison.title")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Smart Input */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{t("onboarding.featureComparison.smartTitle")}</h4>
                <span className="text-xs text-muted-foreground bg-primary/10 px-2 py-0.5 rounded">
                  {t("onboarding.featureComparison.smartBadge")}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("onboarding.featureComparison.smartDesc")}
            </p>
            <div className="flex items-center gap-1 text-xs text-primary">
              <Clock className="h-3 w-3" />
              <span>{t("onboarding.featureComparison.smartSpeed")}</span>
            </div>
          </div>

          {/* Manual Entry */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <Edit3 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{t("onboarding.featureComparison.manualTitle")}</h4>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {t("onboarding.featureComparison.manualBadge")}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("onboarding.featureComparison.manualDesc")}
            </p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{t("onboarding.featureComparison.manualSpeed")}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
